import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { memo, useCallback, useState } from 'react';

import { MessageCardSkeleton } from './MessageCardSkeleton';

import { DraftCard } from '@/components/DraftCard';
import { EmptyTab } from '@/components/EmptyTab';
import { InboxListError, getInboxListErrorType } from '@/components/InboxListError';
import { TabRootFlatList, TabRootListFallback } from '@/components/TabRootList';
import { Divider } from '@/components/ui/divider';
import { FlatList } from '@/components/ui/flat-list';
import { RefreshControl } from '@/components/ui/refresh-control';
import { useChatTransport } from '@/hooks/chat/useChatTransport';
import { type DraftV2 } from '@/services/getDraftV2';
import { getDraftsV2QueryOptions } from '@/services/getDraftsV2';
import { userIdAtom } from '@/services/globalState';
import { Sentry } from '@/util/sentry';

export const MobileDraftsTab = memo(function MobileDraftsTab() {
  const userId = useAtomValue(userIdAtom);

  const transport = useChatTransport();

  const { isLoading, data, error, refetch } = useQuery({
    ...getDraftsV2QueryOptions(userId),
    enabled: !!userId && !!transport,
  });

  const [refreshing, setRefreshing] = useState(false);

  const renderItem = useCallback(({ item }: { item: DraftV2 }) => <DraftCard {...item} />, []);

  const keyExtractor = useCallback((item: DraftV2) => item.uuid, []);

  if (isLoading) {
    return (
      <TabRootListFallback>
        <MessageCardSkeleton />
      </TabRootListFallback>
    );
  }

  if (error) {
    return (
      <TabRootListFallback>
        <InboxListError errorType={getInboxListErrorType(error)} onRetry={() => refetch()} />
      </TabRootListFallback>
    );
  }

  if (!data?.length) {
    return (
      <TabRootListFallback>
        <EmptyTab
          tabKey="drafts"
          tabSelectedKey="drafts"
          animation="POWER_IN_NUMBERS"
          title="We've got you covered"
          description="We'll keep all your unfinished thoughts safe right here."
        />
      </TabRootListFallback>
    );
  }

  return (
    <TabRootFlatList
      className="flex-1"
      showsVerticalScrollIndicator={false}
      data={data || []}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      ItemSeparatorComponent={Divider}
      refreshControl={
        !isWeb ? (
          <RefreshControl
            refreshing={refreshing}
            onRefresh={async () => {
              setRefreshing(true);

              try {
                await refetch();
              } catch (e) {
                Sentry.captureException(e, {
                  tags: { component: 'MobileDraftsTab', action: 'pull-to-refresh' },
                });
              } finally {
                setRefreshing(false);
              }
            }}
          />
        ) : undefined
      }
    />
  );
});
